import { Controller, FieldValues, FieldPath, Control } from "react-hook-form";
import { Input, InputProps } from "../ui/input";
import { TextArea, TextAreaProps } from "../ui/text-area";
import DateInputComponent from "../common/DateInputComponent";
import { DateValue } from "@internationalized/date";

interface ControlledInputProps<T extends FieldValues> extends InputProps {
    name: FieldPath<T>;
    control: Control<T>;
};

const ControlledInput = <T extends FieldValues>(props: ControlledInputProps<T>) => {
    const { control, name, ...inputProps } = props;
	return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <Input
                    className="h-11"
                    {...field}
                    {...inputProps}
                    value={field.value}
                />
            )}
        />
)};

interface ControlledDateInputProps<T extends FieldValues> {
	name: FieldPath<T>;
	control: Control<T>;
	error?: string;
}

const ControlledDateInput = <T extends FieldValues>({
	control,
	name,
	error,
}: ControlledDateInputProps<T>) => {
	return (
		<div className="flex flex-col gap-1 w-full">
        <Controller
				name={name}
				control={control}
				render={({ field }) => (
					<DateInputComponent
						value={field.value as DateValue}
						onChange={(val: DateValue | null) => {
							field.onChange(val);
						}}
					/>
				)}
			/>			
			{error && (
				<span className="text-red-500 text-sm">
					{error}
				</span>
			)}
		</div>
	);
};


interface ControlledTextareaProps<T extends FieldValues> extends Omit<TextAreaProps, "value" | "onChange"> {
	name: FieldPath<T>;
	control: Control<T>;
}

const ControlledTextarea = <T extends FieldValues>(props: ControlledTextareaProps<T>) => {
	const { control, name, ...textareaProps } = props;
	return (
		<Controller
			name={name}
			control={control}
			render={({ field, fieldState }) => (
				<TextArea
					{...field}
					{...textareaProps}
					value={field.value as string}
					error={fieldState.error?.message}
				/>
			)}
		/>
	);
};

export { ControlledInput, ControlledDateInput, ControlledTextarea };